import _debounce from 'lodash.debounce';
import isBreakpoint from '~scripts/utility/media-query';
import waitForElementTransition from 'wait-for-element-transition';
import NavMenuLangSelector from './nav-menu-lang-select';

/**
 * Sidebar menu controller
 */
const NavMenuSidebar = {
  _isOpen: false,

  _depth: 0,

  _selectors: {
    headerNav: '.js-header-nav',
    sidebar: '.js-nav-sidebar',
    sidebarMenuContainer: '.js-nav-sidebar-menu-container',
    sidebarMenu: '.js-nav-sidebar-menu',
    btnOpen: '.js-nav-sidebar-btn-open',
    btnClose: '.js-nav-sidebar-btn-close',
    btnGoBack: '.js-nav-sidebar-btn-go-back',
    menuItem: '.js-nav-menu-item',
    subMenu: '.js-nav-submenu'
  },

  init() {
    const self = this;

    // Event :: open / close buttons
    document.querySelector(this._selectors.btnOpen).addEventListener('click', this.show.bind(this));
    document.querySelector(this._selectors.btnClose).addEventListener('click', this.close.bind(this));

    // Event :: close menu when clicking outside it
    document.querySelector('body').addEventListener('click', this.handleOutsideClick.bind(this));

    // Event :: close sidebar when viewport changes
    window.onresize = _debounce(() => {
      self.handleSidebarVisibility();
    }, 200);

    // Event :: clicking on items with children, reveals the submenu
    const sidebarMenu = document.querySelector(this._selectors.sidebarMenu);
    sidebarMenu.querySelectorAll(this._selectors.menuItem).forEach(node => {
      node.querySelector('a').addEventListener('click', self.handleMenuItemClick.bind(self));
    });

    // Event :: go back one level from the current submenu
    document.querySelector(this._selectors.btnGoBack).addEventListener('click', this.goToPreviousLevel.bind(this));

    // Hack :: creates links that look like headers on the deepest menus
    this.buildHeaderLinkForNestedLists_MegaMenuHack();

    // Initialises LangSelector
    NavMenuLangSelector.init();
  },

  show() {
    const self = this;

    document.querySelector('body').classList.add('no-scroll');
    document.querySelector(this._selectors.sidebar).classList.add('show');
    document.querySelector(this._selectors.headerNav).classList.add('show-overlay');

    // Adds timeout to avoid triggering automatic close event added to body
    setTimeout(() => {
      self._isOpen = true;
    });
  },

  close() {
    document.querySelector(this._selectors.sidebar).classList.remove('show');
    document.querySelector(this._selectors.headerNav).classList.remove('show-overlay');
    document.querySelector('body').classList.remove('no-scroll');

    this.resetMenuNavigation();

    this._isOpen = false;
  },

  goToNextLevel(menuItemEl) {
    this._depth += 1;

    this.resetScrollPosition();

    // 'Mega' Menu HACK :: adds helper class to show/hide menu items
    this.markSubMenuAsActive( menuItemEl.querySelector(this._selectors.subMenu) );

    // Shows submenu
    menuItemEl.querySelector(this._selectors.subMenu).classList.add('show-submenu');

    // Animates menu to the newly revealed submenu
    document.querySelector(this._selectors.sidebarMenu).style.transform = `translateX(-${this._depth * 100}%)`;

    if ( this._depth === 1 ) {
      // Removes helper class, indicating we're navigating into a sub menu
      document.querySelector(this._selectors.sidebar).classList.add('is-navigating-menu');
    }
  },

  goToPreviousLevel() {
    if ( this._depth === 0 ) {
      return;
    }

    this.resetScrollPosition();

    this._depth -= 1;

    // Animates menu to the previous submenu
    const sidebarMenuEl = document.querySelector(this._selectors.sidebarMenu);
    sidebarMenuEl.style.transform = `translateX(-${this._depth * 100}%)`;

    // 'Mega' Menu HACK :: adds helper class to show/hide menu items
    this.markPreviousSubMenuAsActive();

    // Hides submenu we are navigating away from after animation finishes
    waitForElementTransition(sidebarMenuEl)
      .then(() => {
        const visibleSubMenus = document.querySelector(this._selectors.sidebarMenu).querySelectorAll('.show-submenu');
        const deepestChild = visibleSubMenus[visibleSubMenus.length - 1];
        deepestChild.classList.remove('show-submenu');
      })
    ;

    if ( this._depth === 0 ) {
      // Removes helper class, indicating we're back to the top level
      document.querySelector(this._selectors.sidebar).classList.remove('is-navigating-menu');
    }
  },

  /**
   * Scrolls back to the top of menu
   */
  resetScrollPosition() {
    document.querySelector(this._selectors.sidebarMenuContainer).scrollTop = 0;
  },

  resetMenuNavigation() {
    // Hides all submenus
    document
      .querySelector(this._selectors.sidebar)
      .querySelectorAll('.show-submenu')
      .forEach(node => {
        node.classList.remove('show-submenu');
      })
    ;

    this._depth = 0;

    // Removes helper class
    document.querySelector(this._selectors.sidebar).classList.remove('is-navigating-menu');

    // Resets translateX position after sidebar animation finishes
    const sidebarEl = document.querySelector(this._selectors.sidebar);
    waitForElementTransition(sidebarEl)
      .then(() => {
        document.querySelector(this._selectors.sidebarMenu).style.transform = `translateX(0)`;
      })
    ;
  },

  /**
   * Clicking outside the sidebar will close it
   */
  handleOutsideClick(e) {
    if ( this._isOpen ) {
      if ( e.target.closest(this._selectors.sidebar) === null ) {
        this.close();
      }
    }
  },

  /**
   * If not in sidebar breakpoint, close it
   */
  handleSidebarVisibility() {
    if ( ! this._isOpen ) {
      return;
    }

    if ( ! isBreakpoint('nav-mobile') ) {
      this.close();
    }
  },

  /**
   * If clicked item has children, show them.
   * Otherwise the link is followed.
   */
  handleMenuItemClick(e) {
    const menuItemEl = e.currentTarget.closest(this._selectors.menuItem);

    // 'Mega' Menu HACK :: allows navigation to proceed as normal when clicking on sub menu title
    if ( ! this.canGoToNextLevel_MegaMenuHack(e) ) {
      return;
    }

    // Has submenu, reveal it
    if ( menuItemEl.querySelector(this._selectors.subMenu) ) {
      e.preventDefault();

      this.goToNextLevel( menuItemEl );
    }

    // Anchor link, close sidebar menu
    const href = e.target.getAttribute('href');
    if ( href !== '#' && href.indexOf('#') !== -1 ) {
      this.close();
    }
  },

  /**
   * HACK - please refer to explanation on header-nav/index.scss
   */
  canGoToNextLevel_MegaMenuHack(event) {
    const currentSubMenuEl = event.currentTarget.closest('.show-submenu');

    if ( currentSubMenuEl === null ) {
      return true;
    }

    // Checks if clicked element is the same as sub menu title
    let allowContinue = true;
    currentSubMenuEl.querySelectorAll(':scope > li > a').forEach(node => {
      if ( node === event.target ) {
        allowContinue = false;
      }
    });

    return allowContinue;
  },

  /**
   * HACK - please refer to explanation on header-nav/header-nav-mobile.scss
   */
  markPreviousSubMenuAsActive() {
    const sidebarMenuEl = document.querySelector(this._selectors.sidebarMenu);

    if ( sidebarMenuEl.querySelector('.active-submenu') ) {
      // Removes class from previously active sub menu
      const activeSubmenuEl = sidebarMenuEl.querySelector('.active-submenu');
      activeSubmenuEl.classList.remove('active-submenu');

      // Adds class to parent (that we're going back to)
      if ( activeSubmenuEl.parentNode.closest('ul[data-depth="'+this._depth+'"]') ) {
        activeSubmenuEl.parentNode.closest('ul[data-depth="'+this._depth+'"]').classList.add('active-submenu');
      }
    }
  },

  /**
   * HACK - please refer to explanation on header-nav/header-nav-mobile.scss
   */
  markSubMenuAsActive(subMenuEl) {
    const sidebarMenuEl = document.querySelector(this._selectors.sidebarMenu);

    // Removes class from previously active sub menu
    if ( sidebarMenuEl.querySelector('.active-submenu') ) {
      sidebarMenuEl.querySelector('.active-submenu').classList.remove('active-submenu');
    }

    subMenuEl.classList.add('active-submenu');
  },

  /**
   * Hack - creates links that look like headers on the deepest menus
   */
  buildHeaderLinkForNestedLists_MegaMenuHack() {
    const sidebarMenu = document.querySelector(this._selectors.sidebarMenu);

    sidebarMenu.querySelectorAll('ul[data-depth="3"]').forEach(ulNode => {

      // Builds link item from ulNode's parent menu item
      let linkItemNode = document.createElement('a');
      linkItemNode.innerText = ulNode.previousSibling.innerText;
      linkItemNode.href = ulNode.previousSibling.href;

      // Builds list item with <a> element
      let listItemNode = document.createElement('li');
      listItemNode.appendChild(linkItemNode);
      listItemNode.classList.add('nav-menu-item', 'nav-menu-item--header-link');

      // Adds list item to top of the current list
      ulNode.insertBefore(listItemNode, ulNode.firstChild);
    });
  }
};

export default NavMenuSidebar;